import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Radar, Building2, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from '@/components/utils/translations';

// Valid decision window bands - anything else will be filtered out
const VALID_DECISION_WINDOW_BANDS = [
  '<12 months',
  '<12m',
  '12-24 months',
  '12-24m',
  '24+ months',
  '>24 months',
  '24+m',
  '>24m'
];

export default function DecisionWindowRadarFull({ accounts, isLoading }) {
  const { t } = useTranslation();
  
  // Filter accounts with valid decision window bands only
  const accountsWithDecisionWindow = accounts.filter(a => {
    if (a.is_visible === false) return false;
    if (!a.decision_window_band || a.decision_window_band === 'N/A') return false;
    
    // Only include accounts with valid decision window bands
    return VALID_DECISION_WINDOW_BANDS.includes(a.decision_window_band);
  });

  // Group accounts by their exact decision_window_band value
  const accountsByBand = accountsWithDecisionWindow.reduce((acc, account) => {
    const band = account.decision_window_band;
    if (band) {
      if (!acc[band]) {
        acc[band] = [];
      }
      acc[band].push(account);
    }
    return acc;
  }, {});

  // Sort bands logically
  const bandOrder = {
    '<12 months': 1,
    '<12m': 1,
    '12-24 months': 2,
    '12-24m': 2,
    '24+ months': 3,
    '>24 months': 3,
    '24+m': 3,
    '>24m': 3,
  };

  const sortedBands = Object.keys(accountsByBand).sort((a, b) => {
    const orderA = bandOrder[a] || 999;
    const orderB = bandOrder[b] || 999;
    return orderA - orderB;
  });

  return (
    <Card className="border-0 shadow-md h-full">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Radar className="w-5 h-5 text-[#00C2C2]" />
          <CardTitle className="text-lg font-semibold text-[#0A1A2F]">{t('decisionWindowRadar')}</CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="ml-1 p-1 rounded-full hover:bg-gray-100 transition-colors cursor-help"
                  aria-label={t('decisionWindowRadar')}
                >
                  <Info className="w-4 h-4 text-[#5C6B7A]" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs bg-[#0A1A2F] text-white border-0 shadow-lg">
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-[#00C2C2]">{t('decisionWindowMonitor')}</p>
                  <p className="text-sm">
                    {t('decisionWindowDesc')}
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <p className="text-sm text-[#5C6B7A]">{t('accountsApproaching')}</p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {sortedBands.map((bandName) => {
            const bandAccounts = accountsByBand[bandName] || [];
            return (
              <div key={bandName} className="bg-gray-50/70 p-4 rounded-lg">
                <h3 className="font-semibold text-sm text-[#0A1A2F] border-b pb-2 mb-3 text-center">
                  {bandName} ({bandAccounts.length})
                </h3>
                <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
                  {isLoading ? (
                    Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)
                  ) : bandAccounts.length > 0 ? (
                    bandAccounts.map(acc => (
                      <Link to={createPageUrl(`AccountFinal?id=${acc.id}`)} key={acc.id} className="block p-2 rounded-md hover:bg-[#00C2C2]/10 transition-colors">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-[#5C6B7A]" />
                          <span className="text-xs font-medium text-[#0A1A2F] truncate">{acc.account_name}</span>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <p className="text-xs text-center text-[#5C6B7A] py-4">{t('noAccountsInBand')}</p>
                  )}
                </div>
              </div>
            );
          })}
          {sortedBands.length === 0 && !isLoading && (
            <div className="col-span-3 text-center py-8">
              <p className="text-sm text-[#5C6B7A]">{t('noAccountsWithWindows')}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}